import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { Button } from '@/components/button/Button'
import { SearchField } from '@/components/search-field/SearchField'
import { AddSharePointSiteForm } from '@/user_files/external-storage/components/AddSharePointSiteForm'
import { ExternalFolderList } from '@/user_files/external-storage/components/ExternalFolderList'
import { Modal, ModalClose, ModalTitle, ModalDescription } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { useExternalFolderPicker, type UseExternalFolderPickerOptions } from '@/user_files/external-storage/hooks/useExternalFolderPicker'

export function SelectExternalFolderModal(props: UseExternalFolderPickerOptions) {
  const { account, open, onSelect, replaceExistingMapping = false } = props
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]
  const {
    breadcrumbs,
    filteredFolders,
    selectedFolderId,
    unavailableFolderIds,
    search,
    loadingFiles,
    isImporting,
    error,
    showAddSharePointSite,
    canConfirm,
    canRetryBrowse,
    handleClose,
    handleDrillIn,
    handleBreadcrumbNav,
    handleRootBreadcrumbNav,
    handleConnect,
    setSearch,
    setSelectedFolderId,
    retryBrowse,
    markCancelled,
  } = useExternalFolderPicker(props)

  const breadcrumbItems: BreadcrumbItemType[] = [
    {
      id: 'all-folders',
      label: 'All Folders',
      ...(breadcrumbs.length > 0 ? { onClick: handleRootBreadcrumbNav } : {}),
    },
    ...breadcrumbs.map((crumb, index) => ({
      id: crumb.id,
      label: crumb.name,
      ...(index < breadcrumbs.length - 1 ? { onClick: () => handleBreadcrumbNav(index) } : {}),
    })),
  ]

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose()
      }}
    >
      <ModalContainer width="md" height="max-full" stacked={Boolean(onSelect)}>
        <div className="px-6 pb-4 pt-6">
          <ModalTitle>{showAddSharePointSite ? `Select ${providerLabel} Site` : `Select ${providerLabel} Folder`}</ModalTitle>
          <ModalDescription className="mt-1.5 text-base text-brand-neutral-950">
            {showAddSharePointSite
              ? `Search for a ${providerLabel} site by name, or paste a link to it. The address of any page in the site works.`
              : onSelect
              ? `Choose a ${providerLabel} folder. Sync will start after you save the matter.`
              : `Choose a ${providerLabel} folder to sync with this matter.`}
          </ModalDescription>
        </div>

        <div className="min-h-0 flex-1">
          {showAddSharePointSite ? (
            <div className="px-4 pb-4">
              <AddSharePointSiteForm accountId={account.id} onSiteResolved={handleDrillIn} />
            </div>
          ) : (
            <>
              <div className="px-4 pb-2 pt-2">
                <SearchField value={search} onChange={setSearch} placeholder="Search Folders" autoFocus />
              </div>
              <div className="px-4 py-2">
                <Breadcrumbs items={breadcrumbItems} maxItems={3} persistFirstItem />
              </div>
              <ExternalFolderList
                error={error}
                folders={filteredFolders}
                loading={loadingFiles}
                selectedFolderId={selectedFolderId}
                unavailableFolderIds={unavailableFolderIds}
                onDrillIn={handleDrillIn}
                onSelectFolder={setSelectedFolderId}
              />
              {canRetryBrowse && (
                <div className="px-4 pb-4">
                  <Button type="button" variant="outlined" size="sm" onClick={retryBrowse}>
                    Retry loading folders
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t border-brand-neutral-200 p-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
          <ModalClose asChild>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="md"
              className="order-2 w-full sm:order-1 sm:w-auto sm:min-w-[90px]"
              onClick={markCancelled}
            >
              Cancel
            </Button>
          </ModalClose>
          <Button
            type="button"
            disabled={!canConfirm}
            onClick={handleConnect}
            loading={isImporting}
            size="md"
            className="order-1 w-full sm:order-2 sm:w-auto sm:min-w-[120px]"
          >
            {onSelect ? 'Select Folder' : replaceExistingMapping ? 'Change Folder' : 'Connect Folder'}
          </Button>
        </div>
      </ModalContainer>
    </Modal>
  )
}
